import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { SurveyCreatorModel } from 'survey-creator-core';
import { ComponentCollection, Model, Serializer } from 'survey-core';
import 'survey-core/survey.i18n.js';
import 'survey-creator-core/survey-creator-core.i18n.js';
import Swal from 'sweetalert2';

declare const $: any;
import { MatStepper } from '@angular/material/stepper';
import { FormBuilder, Validators } from '@angular/forms';
import { STEPPER_GLOBAL_OPTIONS } from '@angular/cdk/stepper';
import { ActivatedRoute, Router } from '@angular/router';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { HttpErrorResponse } from '@angular/common/http';

import { GptService, TemplateType } from './../../services/gpt/gpt.service';
import {
  CreateQuestionnaireDto,
  CreateSurveyResponseDto,
  QuestionnaireTemplate,
  QuestionnaireType,
  SurveyElement,
  SurveyJSON,
  SurveyQuestion,
} from '../../services/gpt/types';
import { SelectedSubscriberService } from '../../services/selected-subscriber/selected-subscriber.service';
import { ResultSetService } from '../../services/resultSet/resultSet.service';
import { ResultSetResponse } from '../../services/resultSet/types';
import { of } from 'rxjs';
import { switchMap } from 'rxjs/operators';

ComponentCollection.Instance.add({
  name: 'algocytegpt',
  title: 'Algocyte Question',
  elementsJSON: [
    {
      type: 'text',
      titleLocation: 'hidden',
    },
    {
      type: 'descriptivetext',
      name: 'descriptivetext',
      title: 'question title',
      caption: 'Enter',
      diagnosis_hypothisis: 'Enter',
      prognosis: 'Enter',
      score: '--',
      ICDcode: 'Enter',
    },
  ],
  onInit() {
    // Add a property to the custom component for metadata
    Serializer.addProperty('algocytegpt', {
      name: 'metadata',
      type: 'text',
      default: JSON.stringify({
        diagnosis_hypothisis: 'Enter',
        prognosis: 'Enter',
        score: '--',
        ICDcode: 'Enter',
      }),
      category: 'general',
    });
  },
  onLoaded(question) {
    try {
      // Retrieve the "descriptivetext" question
      const descriptivetext =
        question.contentPanel.getQuestionByName('descriptivetext');

      // Parse and assign metadata values to the descriptive text question
      const metadata = JSON.parse(
        question.metadata ||
          JSON.stringify({
            diagnosis_hypothisis: 'Enter',
            prognosis: 'Enter',
            score: '--',
            ICDcode: 'Enter',
          })
      );

      descriptivetext['diagnosis_hypothisis'] = metadata.diagnosis_hypothisis;
      descriptivetext['prognosis'] = metadata.prognosis;
      descriptivetext['score'] = metadata.score;
      descriptivetext['ICDcode'] = metadata.ICDcode;
    } catch (error) {
      console.error('Error loading metadata for algocytegpt:', error);
    }
  },
});

const creatorOptions = {
  showLogicTab: false,
  showPropertyGrid: false,
  isAutoSave: true,
  showToolbox: false,
  haveCommercialLicense: true,
};

@Component({
  selector: 'app-survey-creator',
  templateUrl: './survey-creator.component.html',
  styleUrls: ['./survey-creator.component.css'],
  providers: [
    {
      provide: STEPPER_GLOBAL_OPTIONS,
      useValue: { displayDefaultIndicatorType: false },
    },
  ],
})
export class SurveyCreatorComponent implements OnInit, OnDestroy {
  @ViewChild('stepper') stepper!: MatStepper;

  model: SurveyCreatorModel;
  stepTwoTitle = '';
  stepOneQuestions: TemplateType[] = [];
  selectedPatient: any = JSON.parse(localStorage.getItem('patient'));
  isTemplate: boolean = false;
  stepTwoInfo: any = {
    title: '',
    userInfo: [
      {
        icon: 'account_circle',
        key: `${this.selectedPatient.forename} ${this.selectedPatient.surname}`,
        name: 'Name',
      },
      {
        icon: 'cake',
        key: `${
          new Date().getFullYear() -
          new Date(this.selectedPatient.DOB).getFullYear()
        } Years`,
        name: 'Age',
      },
    ],
    testInfo: [
      {
        icon: 'account_circle',
        key: `${this.selectedPatient.forename} ${this.selectedPatient.surname}`,
        name: 'Name',
      },
    ],
  };

  firstFormGroup = this._formBuilder.group({
    firstCtrl: ['', Validators.required],
  });

  stepTwoPrompt: string;
  sanitizedDescription: SafeHtml;
  suggestions: CreateSurveyResponseDto;
  formFields: any;
  previewMode: boolean = false;
  surveyModel: Model;
  isPublishing: boolean;
  loadingQuestion: boolean;
  publishQuestion: boolean;
  questionnaireTitle: any;
  selectedReportDate: any;
  subscriberId: number;
  resultSetId: number;
  resultSet?: ResultSetResponse;
  selectedType: TemplateType = null;
  selectedTemplate: QuestionnaireTemplate | null = null;
  templates: QuestionnaireTemplate[] = [];

  protected readonly QuestionnaireType = QuestionnaireType;

  constructor(
    private router: ActivatedRoute,
    private _formBuilder: FormBuilder,
    private route: Router,
    private sanitizer: DomSanitizer,
    private gptService: GptService,
    private resultSetService: ResultSetService,
    private selectedSubscriberService: SelectedSubscriberService
  ) {
    this.resultSetId = +this.router.snapshot.paramMap.get('testId');
    this.subscriberId = this.selectedSubscriberService.user?.id;

    this.selectedSubscriberService.getUserDataObservable().subscribe((user) => {
      this.subscriberId = user ? user.id : null;
    });
  }

  ngOnInit() {
    this.resultSetService.getResultSetById(this.resultSetId).subscribe({
      next: (resultSet) => {
        this.resultSet = resultSet;
        this.stepOneQuestions = this.gptService.getGptTypes(resultSet);
      },
    });

    this.gptService.getTemplates({ pageSize: 100, pageOffset: 0 }).subscribe({
      next: ({ results }) => {
        this.templates = results;
      },
    });
  }

  viewQuestionnaire() {
    const survey = new Model(this.formFields);
    this.surveyModel = survey;
  }

  createQuestionnaire(defaultJson: SurveyJSON) {
    localStorage.setItem('gptMode', 'create');

    const creator = new SurveyCreatorModel(creatorOptions);
    const rawDescription =
      '<p>This is the <strong>description</strong> for Question 1.</p>';
    this.sanitizedDescription =
      this.sanitizer.bypassSecurityTrustHtml(rawDescription);

    creator.text = JSON.stringify(defaultJson);

    creator.saveSurveyFunc = (_saveNo: number, _callback: Function) => {
      this.formFields = JSON.parse(creator.text);
    };

    creator.allowEditExpressionsInTextEditor = true;

    creator.autoSaveDelay = 100;

    creator.haveCommercialLicense = true;
    creator.toolbox.removeItem('descriptivetext');
    this.model = creator;
    this.loadingQuestion = false;
  }

  checkValue(templateType: TemplateType, index: number) {
    this.isTemplate = false;
    this.selectedTemplate = null;
    this.suggestions = null;
    this.selectedType = templateType.isChecked ? templateType : null;

    if (templateType.isChecked) {
      // Current Is Selected
      this.stepOneQuestions.forEach((que, i) => {
        if (index != i) {
          if (que.isChecked) que.isChecked = false;
        } else {
          if (templateType.type === QuestionnaireType.PROFILE_BASED_AI) {
            this.questionnaireTitle = templateType.title;
            this.stepTwoTitle = `<b> Profile - based </b> questionnaire are only based on the profile data. Click
                                        “Generate” to launch the questionnaire`;
          } else if (templateType.type === QuestionnaireType.RESULT_BASED_AI) {
            // Test Base
            this.questionnaireTitle = templateType.title;
            this.stepTwoTitle = `<b> Test - based </b> questionnaire will consider the profile of the patient as well as the test listed below.Confirm the details then click on “Generate” to launch the questionnaire`;
          } else if (templateType.type === QuestionnaireType.MANUAL) {
            this.questionnaireTitle = templateType.title;
            this.stepTwoPrompt = '';
            this.stepTwoTitle = ``;
          } else if (templateType.type === QuestionnaireType.TEMPLATE_BASED) {
            this.questionnaireTitle = templateType.title;
            this.stepTwoPrompt = '';
            this.stepTwoTitle = ``;
          }
        }
      });
    }
  }

  disableNext(): boolean {
    return (
      !this.isTypeSelected() ||
      this.loadingQuestion ||
      (this.isTemplateSpecific && !this.selectedTemplate)
    );
  }

  goToBack(location?: string) {
    if (location === 'dashboard') {
      this.route.navigate(['/' + 'dashboard']);
    }
  }

  isTypeSelected(): boolean {
    return !!this.selectedType;
  }

  typeSelected() {
    if (!this.selectedType) {
      return;
    }
    this.generatePrompt(this.selectedType.type);
  }

  get isProfileSpecific(): boolean {
    return this.selectedType?.type === QuestionnaireType.PROFILE_BASED_AI;
  }

  get isResultSpecific(): boolean {
    return this.selectedType?.type === QuestionnaireType.RESULT_BASED_AI;
  }

  get isTemplateSpecific(): boolean {
    return this.selectedType?.type === QuestionnaireType.TEMPLATE_BASED;
  }

  showPromptWriting(): boolean {
    return this.isResultSpecific || this.isProfileSpecific;
  }

  generatePrompt(type: QuestionnaireType) {
    this.loadingQuestion = true;

    if (type === QuestionnaireType.MANUAL) {
      this.stepper.next();
      this.formFields = {
        title: this.questionnaireTitle,
        pages: [
          {
            elements: [
              {
                type: 'text',
                name: 'Question 1',
              },
            ],
          },
        ],
      };

      this.createQuestionnaire(this.formFields);
      return;
    } else if (type === QuestionnaireType.TEMPLATE_BASED) {
      this.stepper.next();
      this.formFields = this.selectedTemplate?.questionnaireSchema;
      this.createQuestionnaire(this.formFields);
      return;
    } else {
      this.gptService
        .getPrompt({
          type,
          resultSetId: this.resultSetId,
          userId: this.subscriberId,
        })
        .subscribe({
          next: (response) => {
            this.stepTwoPrompt = response.prompt;
            this.stepper.next();
            this.loadingQuestion = false;
          },
          error: (err) => {
            this.loadingQuestion = false;

            this.showErrorDialog(
              'Failed to generate a prompt',
              (err as HttpErrorResponse).error?.errors ||
                'Please contact support or try again'
            );
          },
        });
    }
  }

  showErrorDialog(title: string, text: string) {
    Swal.fire({
      type: 'warning',
      title,
      text,
    });
  }

  async generate() {
    const type = this.selectedType.type;
    this.loadingQuestion = true;
    this.stepper.next();

    if (type === QuestionnaireType.MANUAL) {
      const surveyJson: SurveyJSON = {
        title: this.questionnaireTitle,
        logoPosition: 'right',
        pages: [
          {
            name: '',
            title: '',
            elements: [
              {
                type: 'text',
                name: 'demo input',
                title: '',
              },
            ],
          },
        ],
      };

      this.createQuestionnaire(surveyJson);
    } else {
      this.gptService
        .getSuggestions({
          type,
          message: this.stepTwoPrompt,
          userId: this.subscriberId,
        })
        .subscribe({
          next: (suggestions) => {
            this.suggestions = suggestions;
            this.formFields = this.suggestedQuestionnaireToSurveyCreator(
              this.suggestions.followUpQuestions
            );
            this.formFields.title = this.questionnaireTitle;
            this.createQuestionnaire(this.formFields);
          },
          error: (err) => {
            this.showErrorDialog(
              'Failed to generate a survey',
              (err as HttpErrorResponse).error?.errors ||
                'Please contact support or try again'
            );
          },
        });
    }
  }

  suggestedQuestionnaireToSurveyCreator(
    suggestedQuestions: SurveyQuestion[]
  ): SurveyJSON {
    const elements: SurveyElement[] = [];

    suggestedQuestions.forEach((suggestion) => {
      const element: SurveyElement = {
        type: 'algocytegpt',
        name: suggestion.question,
        title: `${suggestion.question}`,
        metadata: JSON.stringify({
          diagnosis_hypothisis: suggestion.hypothesis,
          prognosis: suggestion.prognosis,
          score: String(suggestion.score),
          ICDcode: suggestion.ICDcode || 'ABCD',
        }),
      };

      elements.push(element);
    });

    return {
      title: '',
      description: '',
      pages: [
        {
          name: '',
          title: '',
          elements,
        },
      ],
    };
  }

  preview() {
    localStorage.setItem('gptMode', 'view');
    this.previewMode = true;
    this.viewQuestionnaire();
  }

  editMode() {
    localStorage.setItem('gptMode', 'create');
    this.previewMode = false;
  }

  publish() {
    this.isPublishing = true;
    const templateObservable = this.isTemplate
      ? this.gptService.createTemplate({
          questionnaireSchema: this.formFields,
        })
      : of(null);

    templateObservable
      .pipe(
        switchMap((savedTemplate: QuestionnaireTemplate | null) => {
          const questionnaireData: CreateQuestionnaireDto = {
            type: this.selectedType.type,
            userId: this.subscriberId,
            resultSetId: this.resultSetId,
            questionnaireSchema: this.formFields,
            algocyteGptOutput: this.suggestions,
            templateId: savedTemplate?.id,
          };

          return this.gptService.createQuestionnaire(questionnaireData);
        })
      )
      .subscribe({
        next: () => {
          this.publishQuestion = true;
          this.isPublishing = false;
        },
        error: (err) => {
          this.isPublishing = false;
          this.showErrorDialog(
            'Failed to publish a questionnaire',
            (err as HttpErrorResponse).error?.errors ||
              'Please contact support or try again'
          );
        },
      });
  }

  onBackToDashboard() {
    this.route.navigate(['/']);
  }

  ngOnDestroy() {
    localStorage.setItem('gptMode', 'view');
  }
}
